import {
  mapApiResourceToConsentItem,
  type ConsentStatusItem,
  type ConsentStatusItemApiResource,
} from '@/api/consents/consentsApi.types';
import { mapApiResourceToFile, type FileApiResource, type PatientFile } from '@/api/files/filesApi.types';
import {
  mapApiResourceToPreVisitAnswers,
  mapPreVisitAnswerInputs,
  type PreVisitAnswer,
  type PreVisitAnswerApiResource,
  type PreVisitAnswerInput,
} from '@/api/preVisit/preVisitApi.types';

export interface IntakeAnswerApiResource {
  field_code: string;
  section: string;
  label_en: string;
  label_pl: string;
  field_type: string;
  value: string | null;
}

export interface IntakeAnswer {
  fieldCode: string;
  section: string;
  labelEn: string;
  labelPl: string;
  fieldType: string;
  value: string | null;
}

export interface IntakeAnswerInput {
  fieldCode: string;
  value: string | null;
}

interface ConsentStatusApiResource {
  items: ConsentStatusItemApiResource[];
}

export interface OwnerIntakeSubmissionApiResource {
  id: string;
  status: string;
  patient_id: string | null;
  issue_description: string | null;
  proposed_pet_name: string | null;
  proposed_species: string | null;
  proposed_breed: string | null;
  proposed_sex: string | null;
  proposed_date_of_birth: string | null;
  proposed_weight: string | null;
  proposed_neutered: boolean | null;
  proposed_medication: string | null;
  proposed_past_surgeries: string | null;
  proposed_allergies: string | null;
  answers: IntakeAnswerApiResource[];
  pre_visit_answers: PreVisitAnswerApiResource[];
  files: FileApiResource[];
  consent_status: ConsentStatusApiResource;
  review_note: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface IntakeSubmissionApiResource extends OwnerIntakeSubmissionApiResource {
  client_id: string;
  client_name: string;
  reviewed_by_user_id: string | null;
  first_consultation_id: string | null;
}

export interface OwnerIntakeSubmission {
  id: string;
  status: string;
  patientId: string | null;
  issueDescription: string | null;
  proposedPetName: string | null;
  proposedSpecies: string | null;
  proposedBreed: string | null;
  proposedSex: string | null;
  proposedDateOfBirth: string | null;
  proposedWeight: string | null;
  proposedNeutered: boolean | null;
  proposedMedication: string | null;
  proposedPastSurgeries: string | null;
  proposedAllergies: string | null;
  answers: IntakeAnswer[];
  preVisitAnswers: PreVisitAnswer[];
  files: PatientFile[];
  consentStatus: ConsentStatusItem[];
  reviewNote: string | null;
  submittedAt: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IntakeSubmission extends OwnerIntakeSubmission {
  clientId: string;
  clientName: string;
  reviewedByUserId: string | null;
  firstConsultationId: string | null;
}

export interface OwnerIntakeDraftPayload {
  patientId: string | null;
  issueDescription: string | null;
  proposedPetName: string | null;
  proposedSpecies: string | null;
  proposedBreed: string | null;
  proposedSex: string | null;
  proposedDateOfBirth: string | null;
  proposedWeight: string | null;
  proposedNeutered: boolean | null;
  proposedMedication: string | null;
  proposedPastSurgeries: string | null;
  proposedAllergies: string | null;
  answers: IntakeAnswerInput[];
  preVisitAnswers: PreVisitAnswerInput[];
  fileIds: string[];
}

function mapIntakeAnswer(resource: IntakeAnswerApiResource): IntakeAnswer {
  return {
    fieldCode: resource.field_code,
    section: resource.section,
    labelEn: resource.label_en,
    labelPl: resource.label_pl,
    fieldType: resource.field_type,
    value: resource.value,
  };
}

function mapOwnerIntakeCommonFields(
  resource: OwnerIntakeSubmissionApiResource
): OwnerIntakeSubmission {
  return {
    id: resource.id,
    status: resource.status,
    patientId: resource.patient_id,
    issueDescription: resource.issue_description,
    proposedPetName: resource.proposed_pet_name,
    proposedSpecies: resource.proposed_species,
    proposedBreed: resource.proposed_breed,
    proposedSex: resource.proposed_sex,
    proposedDateOfBirth: resource.proposed_date_of_birth,
    proposedWeight: resource.proposed_weight,
    proposedNeutered: resource.proposed_neutered,
    proposedMedication: resource.proposed_medication,
    proposedPastSurgeries: resource.proposed_past_surgeries,
    proposedAllergies: resource.proposed_allergies,
    answers: resource.answers.map(mapIntakeAnswer),
    preVisitAnswers: mapApiResourceToPreVisitAnswers(resource.pre_visit_answers),
    files: resource.files.map(mapApiResourceToFile),
    consentStatus: resource.consent_status.items.map(mapApiResourceToConsentItem),
    reviewNote: resource.review_note,
    submittedAt: resource.submitted_at,
    reviewedAt: resource.reviewed_at,
    createdAt: resource.created_at,
    updatedAt: resource.updated_at,
  };
}

export function mapApiResourceToOwnerIntakeSubmission(
  resource: OwnerIntakeSubmissionApiResource
): OwnerIntakeSubmission {
  return mapOwnerIntakeCommonFields(resource);
}

export function mapApiResourceToIntakeSubmission(
  resource: IntakeSubmissionApiResource
): IntakeSubmission {
  return {
    ...mapOwnerIntakeCommonFields(resource),
    clientId: resource.client_id,
    clientName: resource.client_name,
    reviewedByUserId: resource.reviewed_by_user_id,
    firstConsultationId: resource.first_consultation_id,
  };
}

export function mapOwnerIntakeDraftPayload(payload: OwnerIntakeDraftPayload): Record<string, unknown> {
  return {
    patient_id: payload.patientId,
    issue_description: payload.issueDescription,
    proposed_pet_name: payload.proposedPetName,
    proposed_species: payload.proposedSpecies,
    proposed_breed: payload.proposedBreed,
    proposed_sex: payload.proposedSex,
    proposed_date_of_birth: payload.proposedDateOfBirth,
    proposed_weight: payload.proposedWeight,
    proposed_neutered: payload.proposedNeutered,
    proposed_medication: payload.proposedMedication,
    proposed_past_surgeries: payload.proposedPastSurgeries,
    proposed_allergies: payload.proposedAllergies,
    answers: payload.answers.map(function toApiAnswer(answer) {
      return { field_code: answer.fieldCode, value: answer.value };
    }),
    pre_visit_answers: mapPreVisitAnswerInputs(payload.preVisitAnswers),
    file_ids: payload.fileIds,
  };
}

export function mapIntakeReturnPayload(reviewNote: string): Record<string, unknown> {
  return { review_note: reviewNote };
}

export interface IntakeSubmissionListItemApiResource {
  id: string;
  status: string;
  patient_id: string | null;
  pet_name: string | null;
  is_new_pet: boolean;
  submitted_at: string | null;
  reviewed_at: string | null;
  updated_at: string;
}

export interface WorkspaceIntakeSubmissionListItemApiResource
  extends IntakeSubmissionListItemApiResource {
  client_id: string;
  client_name: string;
}

export interface IntakeSubmissionListItem {
  id: string;
  status: string;
  patientId: string | null;
  petName: string | null;
  isNewPet: boolean;
  submittedAt: string | null;
  reviewedAt: string | null;
  updatedAt: string;
}

export interface WorkspaceIntakeSubmissionListItem extends IntakeSubmissionListItem {
  clientId: string;
  clientName: string;
}

export function mapApiResourceToIntakeSubmissionListItem(
  resource: IntakeSubmissionListItemApiResource
): IntakeSubmissionListItem {
  return {
    id: resource.id,
    status: resource.status,
    patientId: resource.patient_id,
    petName: resource.pet_name,
    isNewPet: resource.is_new_pet,
    submittedAt: resource.submitted_at,
    reviewedAt: resource.reviewed_at,
    updatedAt: resource.updated_at,
  };
}

export function mapApiResourceToWorkspaceIntakeSubmissionListItem(
  resource: WorkspaceIntakeSubmissionListItemApiResource
): WorkspaceIntakeSubmissionListItem {
  return {
    ...mapApiResourceToIntakeSubmissionListItem(resource),
    clientId: resource.client_id,
    clientName: resource.client_name,
  };
}
